// Agency MEMBER session: one login, many workspaces. The agency-session token
// (no org_id) is stashed separately; entering a workspace mints a normal tenant
// token for the member's real user in that workspace.
import axios from 'axios';
import { API_URL } from '@/lib/api';

const SESSION = 'mm_agency_session';
const MEMBER = 'mm_member';
const CURRENT_WS = 'mm_current_ws';
const CACHE = ['userId', 'userName', 'userRole', 'userPermissions'];

// The workspace an agency member is currently working in (shown in the sidebar).
export function currentWorkspaceName() { return localStorage.getItem(CURRENT_WS) || ''; }
// The cached name is written once, when the member enters the workspace. If the
// workspace is later renamed in Settings the cache goes stale, so whoever has a
// live name (the sidebar, from /me) writes it back here.
export function setCurrentWorkspaceName(name) {
  if (name) localStorage.setItem(CURRENT_WS, name);
}

export function startMemberSession(token, member) {
  localStorage.setItem(SESSION, token);
  if (member) localStorage.setItem(MEMBER, JSON.stringify(member));
}
// Public (no session yet): the invitee sets their password from the email link.
export async function acceptInvite(token, password) {
  const { data } = await axios.post(`${API_URL}/api/agency-session/accept-invite`, { token, password });
  return data;
}
export function memberSessionToken() { return localStorage.getItem(SESSION); }
export function inMemberSession() { return !!localStorage.getItem(SESSION); }
export function memberInfo() {
  try { return JSON.parse(localStorage.getItem(MEMBER) || 'null'); } catch { return null; }
}

function authCfg() { return { headers: { Authorization: `Bearer ${memberSessionToken()}` } }; }

// Everything the console shows (workspaces, stats, team if owner).
export async function fetchConsole() {
  const { data } = await axios.get(`${API_URL}/api/agency-session/console`, authCfg());
  return data;
}
export async function fetchMemberWorkspaces() {
  const { data } = await fetchConsole().then((d) => ({ data: { member: d.member, workspaces: d.workspaces } }));
  return data;
}
// Owner-only management calls (all against the agency-session token).
export const agencyApi = {
  addWorkspace: (name, workspace_type) => axios.post(`${API_URL}/api/agency-session/workspaces`, { name, workspace_type }, authCfg()),
  // Billing: the agency is the customer; its client workspaces are covered by
  // whichever tier it buys, so this is the only place its money is taken.
  // Your own account. Same three operations a workspace user gets at
  // /api/profile, backed by the same service, so neither account type ends
  // up with weaker rules than the other.
  // Who can open which Hive, grouped the same way seats are counted.
  roster: () => axios.get(`${API_URL}/api/agency-session/roster`, authCfg()).then((r) => r.data),
  // Each client's last 30 days and signal health (the console's figures).
  pulse: () => axios.get(`${API_URL}/api/agency-session/pulse`, authCfg()).then((r) => r.data),
  profile: () => axios.get(`${API_URL}/api/agency-session/profile`, authCfg()).then((r) => r.data),
  updateProfile: (body) => axios.patch(`${API_URL}/api/agency-session/profile`, body, authCfg()).then((r) => r.data),
  changePassword: (body) => axios.post(`${API_URL}/api/agency-session/profile/password`, body, authCfg()).then((r) => r.data),
  billing: () => axios.get(`${API_URL}/api/agency-session/billing`, authCfg()).then((r) => r.data),
  subscribe: (plan, cycle) => axios.post(`${API_URL}/api/agency-session/billing/subscribe`, { plan, cycle }, authCfg()).then((r) => r.data),
  cancelBilling: () => axios.post(`${API_URL}/api/agency-session/billing/cancel`, {}, authCfg()),
  transferWorkspace: (orgId, body) => axios.post(`${API_URL}/api/agency-session/workspaces/${orgId}/transfer`, body, authCfg()),
  promotable: () => axios.get(`${API_URL}/api/agency-session/members/promotable`, authCfg()).then((r) => r.data),
  addMember: (body) => axios.post(`${API_URL}/api/agency-session/members`, body, authCfg()),
  inviteMember: (body) => axios.post(`${API_URL}/api/agency-session/members/invite`, body, authCfg()),
  resendInvite: (id) => axios.post(`${API_URL}/api/agency-session/members/${id}/resend-invite`, {}, authCfg()),
  setMemberPassword: (id, password) => axios.post(`${API_URL}/api/agency-session/members/${id}/set-password`, { password }, authCfg()),
  promote: (user_id) => axios.post(`${API_URL}/api/agency-session/members/promote`, { user_id }, authCfg()),
  assign: (id, org_id, role) => axios.post(`${API_URL}/api/agency-session/members/${id}/workspaces`, { org_id, role }, authCfg()),
  unassign: (id, org_id) => axios.delete(`${API_URL}/api/agency-session/members/${id}/workspaces/${org_id}`, authCfg()),
  removeMember: (id) => axios.delete(`${API_URL}/api/agency-session/members/${id}`, authCfg()),
  // Lead routing: which client workspace each Meta instant form feeds.
  leadRoutes: (refresh = false) => axios.get(
    `${API_URL}/api/agency-session/lead-routes${refresh ? '?refresh=1' : ''}`, authCfg()).then((r) => r.data),
  setLeadRoute: (body) => axios.post(`${API_URL}/api/agency-session/lead-routes`, body, authCfg()),
  removeLeadRoute: (id) => axios.delete(`${API_URL}/api/agency-session/lead-routes/${id}`, authCfg()),
  // The agency's own Facebook connection (the page is shared by every client,
  // so it belongs here rather than inside one client's workspace).
  metaSettings: () => axios.get(`${API_URL}/api/agency-session/meta-settings`, authCfg()).then((r) => r.data),
  saveMetaSettings: (body) => axios.put(`${API_URL}/api/agency-session/meta-settings`, body, authCfg()),
  resolveMetaPages: (system_user_token) => axios.post(`${API_URL}/api/agency-session/meta-leads/resolve-pages`, { system_user_token }, authCfg()).then((r) => r.data),
  saveMetaPages: (pages) => axios.put(`${API_URL}/api/agency-session/meta-leads/pages`, { pages }, authCfg()).then((r) => r.data),
  removeMetaPage: (id) => axios.delete(`${API_URL}/api/agency-session/meta-leads/pages/${id}`, authCfg()),
  metaHealth: () => axios.get(`${API_URL}/api/agency-session/meta-leads/health`, authCfg()).then((r) => r.data),
  // The agency's WhatsApp Business account: one WABA held here, its phone
  // numbers assigned out one per client workspace.
  whatsapp: () => axios.get(`${API_URL}/api/agency-session/whatsapp`, authCfg()).then((r) => r.data),
  saveWhatsapp: (body) => axios.put(`${API_URL}/api/agency-session/whatsapp`, body, authCfg()).then((r) => r.data),
  waVerifyToken: () => axios.post(`${API_URL}/api/agency-session/whatsapp/verify-token`, {}, authCfg()).then((r) => r.data),
  assignWaNumber: (body) => axios.post(`${API_URL}/api/agency-session/whatsapp/numbers`, body, authCfg()).then((r) => r.data),
  updateWaNumber: (id, body) => axios.patch(`${API_URL}/api/agency-session/whatsapp/numbers/${id}`, body, authCfg()).then((r) => r.data),
  removeWaNumber: (id) => axios.delete(`${API_URL}/api/agency-session/whatsapp/numbers/${id}`, authCfg()).then((r) => r.data),
};

// Back to the console without fully signing out (drop the workspace token, keep
// the agency session).
export function backToConsole() {
  localStorage.removeItem('token');
  localStorage.removeItem(CURRENT_WS);
  CACHE.forEach((k) => localStorage.removeItem(k));
  window.location.assign('/app');
}

// Enter a workspace: swap in a tenant token for the member's real user there.
// `path` lands the member somewhere specific inside the workspace (e.g.
// '/integrations?open=meta_lead_ads' to connect Facebook), instead of the dashboard.
export async function enterWorkspace(orgId, name, path = '') {
  const token = memberSessionToken();
  const { data } = await axios.post(`${API_URL}/api/agency-session/enter/${orgId}`, {}, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!data?.token) throw new Error('Could not enter that workspace');
  localStorage.setItem('token', data.token);
  if (name) localStorage.setItem(CURRENT_WS, name);
  CACHE.forEach((k) => localStorage.removeItem(k));
  window.location.assign(`/app${path || ''}`);
}

// Full sign-out of the agency member (clears the workspace + the agency session).
export function endMemberSession() {
  ['token', SESSION, MEMBER, CURRENT_WS, ...CACHE].forEach((k) => localStorage.removeItem(k));
  window.location.assign('/app');
}
